import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  countActiveFacets, nextSortState, parseFacets,
  type FacetValues, type SortDir, type SortState,
} from "./table-query.js";

export interface UseTableQueryOptions<K extends string> {
  /** Column key used when the URL carries none. Must be server-declared. */
  defaultSort: string;
  defaultDir: SortDir;
  /**
   * Facet param names this page understands. Anything else in the URL is
   * ignored. Pass the page config's `facetKeys` (literal-typed — see
   * `configs.ts`) so `K` is the page's real facet-key union and `setFacet`
   * rejects a key the page never declared.
   */
  facetKeys: readonly K[];
}

/**
 * Sort + filter + page state for a CRM list, held in the URL query string so a
 * refresh, the back button and a pasted link all reproduce the same view.
 *
 * Follows `useTabParam`'s conventions deliberately: state is DERIVED from the
 * params (no duplicate `useState` to fall out of sync), a value equal to its
 * default is omitted so the landing URL stays clean, and writes use history
 * `replace` so Back leaves the page instead of walking every sort click.
 *
 * Changing the sort or any facet resets `page` to 1 — otherwise you keep the
 * old page number against a newly-narrowed result set and land on an empty
 * table.
 *
 * `write()` accumulates mutations in a ref rather than relying on the
 * `(prev) => next` updater form of `setSearchParams`. React Router's actual
 * implementation (v7.15) rebuilds that `prev` from the CURRENT RENDER's
 * `location.search` on every call — it does NOT chain onto a prior call's
 * result the way React's own `setState(prev => next)` does. Two setters
 * called back-to-back in the same synchronous tick (no render in between,
 * e.g. a "clear sort and clear filters" button, or two `setFacet` calls)
 * would otherwise both read the same stale snapshot and the second call
 * would silently clobber the first. `pendingRef` carries the in-progress
 * `URLSearchParams` across such calls, keyed on the `searchParams` instance
 * it was built from — `useSearchParams` memoizes that instance on
 * `location.search`, so it only changes identity once a write actually
 * commits and the URL updates, which is exactly when the accumulator should
 * reset and start fresh from the new committed state.
 */
export function useTableQuery<K extends string>(
  { defaultSort, defaultDir, facetKeys }: UseTableQueryOptions<K>,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = searchParams.get("sort") || defaultSort;

  /**
   * A MISSING `dir` falls back to the caller's `defaultDir`, not to a
   * hardcoded "asc".
   *
   * This has to agree with `write()` below, which DELETES `dir` whenever it
   * equals `defaultDir` to keep the URL readable. Reading a missing `dir` as
   * "asc" contradicted that on any `defaultDir: "desc"` page — Invoices and
   * Quotes are the only two — and the contradiction was visible in the
   * product:
   *
   *   * a bare `/crm/invoices` asked the API for `created_at ASC`, oldest
   *     first, the opposite of the default the page declares;
   *   * clicking the "Created" header wrote `dir=desc`, `write()` erased it as
   *     equal to the default, and it read back as "asc" — THE CLICK DID
   *     NOTHING VISIBLE, which is the exact defect this whole table effort
   *     exists to remove;
   *   * `created_at DESC` was unreachable through the UI at all.
   *
   * Only a `dir` the URL actually carries is honoured; anything other than the
   * two legal values is treated as absent, so a hand-edited `?dir=sideways`
   * lands on the page default rather than silently on "asc".
   *
   * Note this is deliberately NOT the same question as `sortExplicit` below: a
   * bare URL reads `defaultDir` for DIRECTION while still reporting that
   * nobody chose a sort, so search-relevance ranking is unaffected.
   */
  const dirParam = searchParams.get("dir");
  const dir: SortDir = dirParam === "desc" || dirParam === "asc" ? dirParam : defaultDir;

  /**
   * Whether the viewer has actually PICKED a sort, as opposed to landing on
   * the page default. `sort` above cannot answer this — it resolves to
   * `defaultSort` either way — and that ambiguity silently killed
   * search-relevance ranking on three list pages, because every request then
   * carried a `sort=` and the crm-api's `unless sort_requested?` branch could
   * never fire. See `buildTableParams`, which is where the rule lives.
   *
   * `dir` counts as well as `sort`: `write()` drops whichever of the pair
   * equals its default, so clicking a header that happens to land on
   * `defaultSort` in the NON-default direction leaves only `?dir=` behind —
   * still an explicit choice. It counts only when it is one of the two LEGAL
   * values, matching how `dir` itself is read above: a `?dir=` the hook
   * ignores for direction must not count as a choice for ranking either.
   *
   * Landing back on BOTH defaults leaves a bare URL, which is by design
   * indistinguishable from never having sorted (pinned by "omits the sort
   * params when they equal the defaults"). That is the right reading: the
   * page default while searching IS relevance.
   */
  const sortExplicit =
    searchParams.has("sort") || dirParam === "asc" || dirParam === "desc";
  const page = Math.max(1, Math.floor(Number(searchParams.get("page"))) || 1);

  const facets = useMemo(
    () => parseFacets(searchParams, facetKeys),
    [searchParams, facetKeys],
  );

  const pendingRef = useRef<{ source: URLSearchParams; params: URLSearchParams } | null>(null);

  const write = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      // Reuse the in-flight accumulator only while it was built from the
      // still-current committed params; otherwise start a fresh one.
      let pending = pendingRef.current;
      if (!pending || pending.source !== searchParams) {
        pending = { source: searchParams, params: new URLSearchParams(searchParams) };
        pendingRef.current = pending;
      }

      mutate(pending.params);

      // Drop params that equal their default so the URL stays readable.
      if (pending.params.get("sort") === defaultSort) pending.params.delete("sort");
      if (pending.params.get("dir") === defaultDir) pending.params.delete("dir");
      if (pending.params.get("page") === "1") pending.params.delete("page");

      setSearchParams(new URLSearchParams(pending.params), { replace: true });
    },
    [searchParams, setSearchParams, defaultSort, defaultDir],
  );

  const toggleSort = useCallback(
    (column: string) => {
      const nextState: SortState = nextSortState({ sort, dir }, column);
      write((next) => {
        next.set("sort", nextState.sort);
        next.set("dir", nextState.dir);
        next.set("page", "1");
      });
    },
    [sort, dir, write],
  );

  const setFacet = useCallback(
    (key: K, values: string[]) => {
      write((next) => {
        if (values.length > 0) next.set(key, values.join(","));
        else next.delete(key);
        next.set("page", "1");
      });
    },
    [write],
  );

  const clearFacets = useCallback(() => {
    write((next) => {
      for (const key of facetKeys) next.delete(key);
      next.set("page", "1");
    });
  }, [write, facetKeys]);

  const setPage = useCallback(
    (value: number) => write((next) => next.set("page", String(value))),
    [write],
  );

  return {
    sort, dir, sortExplicit, page, facets,
    toggleSort, setFacet, clearFacets, setPage,
    activeFilterCount: countActiveFacets(facets),
  };
}
